import React, { useMemo } from 'react';
import { assets, useAssets } from '@atlassian/global-shortcuts/assets';
import { confluence, useConfluence } from '@atlassian/global-shortcuts/confluence';
import { loom, useLoom } from '@atlassian/global-shortcuts/loom';
import { goals, useGoals } from '@atlassian/global-shortcuts/goals';
import { opsgenie, useOpsgenie } from '@atlassian/global-shortcuts/opsgenie';
import { projects, useProjects } from '@atlassian/global-shortcuts/projects';
import {
	L1_MENU_ID,
	USER_NAVIGATION_ITEM_LINKS,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { GlobalAppShortcuts } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-global-app-shortcuts/src/index.tsx';
import {
	getLicensedProducts,
	LICENSED_PRODUCTS,
} from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import { useLicenseStates } from '@atlassian/jira-tenant-context-controller/src/components/license-states/index.tsx';
import type { GlobalShortcutsItem } from '@atlassian/global-shortcuts';
import { fg } from '@atlassian/jira-feature-gating';

export const useLicenseFilterMap = (teamsAppConfig: GlobalShortcutsItem | undefined) =>
	fg('navx-932-migrate-to-hooks-jira')
		? // eslint-disable-next-line react-hooks/react-compiler,react-hooks/rules-of-hooks
			useLicenseFilterMapNew(teamsAppConfig)
		: // eslint-disable-next-line react-hooks/react-compiler,react-hooks/rules-of-hooks
			useLicenseFilterMapOld(teamsAppConfig);

const useLicenseFilterMapNew = (teamsAppConfig: GlobalShortcutsItem | undefined) => {
	const licenceStates = useLicenseStates();
	const products = getLicensedProducts();
	const hasConfluence = products[LICENSED_PRODUCTS.CONFLUENCE];
	const hasLoom = products[LICENSED_PRODUCTS.LOOM];

	const confluenceItem = useConfluence('');
	const loomItem = useLoom('');
	const assetsItem = useAssets(USER_NAVIGATION_ITEM_LINKS[L1_MENU_ID.GLOBAL_APP_ASSETS]);
	const opsgenieItem = useOpsgenie('');
	const goalsItem = useGoals(USER_NAVIGATION_ITEM_LINKS[L1_MENU_ID.GLOBAL_APP_GOALS]);
	const projectsItem = useProjects(USER_NAVIGATION_ITEM_LINKS[L1_MENU_ID.GLOBAL_APP_PROJECTS]);

	return useMemo(() => {
		const assetsItemWithComponent = { ...assetsItem, component: AssetsComponent };

		if (licenceStates.core === 'ACTIVE') {
			if (licenceStates.serviceDesk === 'ACTIVE') {
				return {
					...(hasConfluence ? { [L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: confluenceItem } : {}),
					...(hasLoom ? { [L1_MENU_ID.GLOBAL_APP_LOOM]: loomItem } : {}),
					[L1_MENU_ID.GLOBAL_APP_ASSETS]: assetsItemWithComponent,
					[L1_MENU_ID.GLOBAL_APP_OPSGENIE]: opsgenieItem,
					...(teamsAppConfig ? { [L1_MENU_ID.GLOBAL_APP_TEAMS]: teamsAppConfig } : {}),
					[L1_MENU_ID.GLOBAL_APP_GOALS]: goalsItem,
				};
			}

			if (licenceStates.productDiscovery === 'ACTIVE') {
				return {
					...(hasConfluence ? { [L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: confluenceItem } : {}),
					...(hasLoom ? { [L1_MENU_ID.GLOBAL_APP_LOOM]: loomItem } : {}),
					...(teamsAppConfig ? { [L1_MENU_ID.GLOBAL_APP_TEAMS]: teamsAppConfig } : {}),
					[L1_MENU_ID.GLOBAL_APP_GOALS]: goalsItem,
				};
			}
		} else {
			if (licenceStates.serviceDesk === 'ACTIVE') {
				return {
					[L1_MENU_ID.GLOBAL_APP_ASSETS]: assetsItemWithComponent,
					[L1_MENU_ID.GLOBAL_APP_OPSGENIE]: opsgenieItem,
					...(teamsAppConfig ? { [L1_MENU_ID.GLOBAL_APP_TEAMS]: teamsAppConfig } : {}),
					[L1_MENU_ID.GLOBAL_APP_GOALS]: goalsItem,
				};
			}

			if (licenceStates.productDiscovery === 'ACTIVE') {
				return {
					...(teamsAppConfig ? { [L1_MENU_ID.GLOBAL_APP_TEAMS]: teamsAppConfig } : {}),
					[L1_MENU_ID.GLOBAL_APP_GOALS]: goalsItem,
				};
			}
		}

		return {
			...(hasConfluence ? { [L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: confluenceItem } : {}),
			...(hasLoom ? { [L1_MENU_ID.GLOBAL_APP_LOOM]: loomItem } : {}),
			...(teamsAppConfig ? { [L1_MENU_ID.GLOBAL_APP_TEAMS]: teamsAppConfig } : {}),
			[L1_MENU_ID.GLOBAL_APP_GOALS]: goalsItem,
			[L1_MENU_ID.GLOBAL_APP_PROJECTS]: projectsItem,
		};
	}, [
		assetsItem,
		confluenceItem,
		goalsItem,
		hasConfluence,
		hasLoom,
		licenceStates.core,
		licenceStates.productDiscovery,
		licenceStates.serviceDesk,
		loomItem,
		opsgenieItem,
		projectsItem,
		teamsAppConfig,
	]);
};

// Filter product shortcuts based on licence states
function useLicenseFilterMapOld(teamsAppConfig: GlobalShortcutsItem | undefined) {
	const licenceStates = useLicenseStates();
	const products = getLicensedProducts();
	const hasConfluence = products[LICENSED_PRODUCTS.CONFLUENCE];
	const hasLoom = products[LICENSED_PRODUCTS.LOOM];

	return useMemo((): { [key: string]: GlobalShortcutsItem } => {
		// Map shortcut configs to menu identifiers
		const shortcutsMap = {
			[L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: confluence(() => ''),
			[L1_MENU_ID.GLOBAL_APP_LOOM]: loom(() => ''),
			[L1_MENU_ID.GLOBAL_APP_GOALS]: goals(() => 'https://home.atlassian.com/goals'),
			[L1_MENU_ID.GLOBAL_APP_PROJECTS]: projects(() => 'https://home.atlassian.com/projects'),
			...(teamsAppConfig ? { [L1_MENU_ID.GLOBAL_APP_TEAMS]: teamsAppConfig } : {}),
			[L1_MENU_ID.GLOBAL_APP_ASSETS]: {
				...assets(() => '/jira/assets'),
				component: AssetsComponent,
			},
			[L1_MENU_ID.GLOBAL_APP_OPSGENIE]: opsgenie(() => ''),
		};

		if (licenceStates.core === 'ACTIVE') {
			if (licenceStates.serviceDesk === 'ACTIVE') {
				return {
					...(hasConfluence ? { [L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: confluence(() => '') } : {}),
					...(hasLoom ? { [L1_MENU_ID.GLOBAL_APP_LOOM]: loom(() => '') } : {}),
					[L1_MENU_ID.GLOBAL_APP_ASSETS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_ASSETS],
					[L1_MENU_ID.GLOBAL_APP_OPSGENIE]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_OPSGENIE],
					...(teamsAppConfig ? { [L1_MENU_ID.GLOBAL_APP_TEAMS]: teamsAppConfig } : {}),
					[L1_MENU_ID.GLOBAL_APP_GOALS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_GOALS],
				};
			}

			if (licenceStates.productDiscovery === 'ACTIVE') {
				return {
					...(hasConfluence ? { [L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: confluence(() => '') } : {}),
					...(hasLoom ? { [L1_MENU_ID.GLOBAL_APP_LOOM]: loom(() => '') } : {}),
					...(teamsAppConfig ? { [L1_MENU_ID.GLOBAL_APP_TEAMS]: teamsAppConfig } : {}),
					[L1_MENU_ID.GLOBAL_APP_GOALS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_GOALS],
				};
			}
		} else {
			if (licenceStates.serviceDesk === 'ACTIVE') {
				return {
					[L1_MENU_ID.GLOBAL_APP_ASSETS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_ASSETS],
					[L1_MENU_ID.GLOBAL_APP_OPSGENIE]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_OPSGENIE],
					...(teamsAppConfig ? { [L1_MENU_ID.GLOBAL_APP_TEAMS]: teamsAppConfig } : {}),
					[L1_MENU_ID.GLOBAL_APP_GOALS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_GOALS],
				};
			}

			if (licenceStates.productDiscovery === 'ACTIVE') {
				return {
					...(teamsAppConfig ? { [L1_MENU_ID.GLOBAL_APP_TEAMS]: teamsAppConfig } : {}),
					[L1_MENU_ID.GLOBAL_APP_GOALS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_GOALS],
				};
			}
		}

		return {
			...(hasConfluence ? { [L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: confluence(() => '') } : {}),
			...(hasLoom ? { [L1_MENU_ID.GLOBAL_APP_LOOM]: loom(() => '') } : {}),
			...(teamsAppConfig ? { [L1_MENU_ID.GLOBAL_APP_TEAMS]: teamsAppConfig } : {}),
			[L1_MENU_ID.GLOBAL_APP_GOALS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_GOALS],
			[L1_MENU_ID.GLOBAL_APP_PROJECTS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_PROJECTS],
		};
	}, [licenceStates, teamsAppConfig, hasConfluence, hasLoom]);
}

const AssetsComponent = () => <GlobalAppShortcuts menuId={L1_MENU_ID.GLOBAL_APP_ASSETS} />;
